import React, { useContext, useEffect, useState } from 'react';
import { Button, Grid, InputAdornment, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { customerFacingPriceFormatter } from '../utils';
import { GiftCardAmountContext } from '../../storefront/giftCardPage/GiftCardAmountContext';
import { amountRangeLabel } from '../../storefront/giftCardPage/giftCardUtils';

type GiftCardAmountStepProps = {
  setShouldDisableNext: (value: boolean) => void;
};

const GiftCardAmountStep = ({ setShouldDisableNext }: GiftCardAmountStepProps) => {
  const { t } = useTranslation();
  const { orderPageState, setOrderPageState } = useOrderPage();
  const reportAmount = useContext(GiftCardAmountContext);
  const { account, details } = orderPageState;
  const settings = account.monetary_voucher_settings;
  const presetAmounts = settings?.preset_amounts ?? [];
  const minAmount = settings?.min_amount ?? 0;
  const maxAmount = settings?.max_amount ?? Infinity;
  const [customAmount, setCustomAmount] = useState(
    details.price > 0 && !presetAmounts.includes(details.price) ? String(details.price) : '',
  );
  const isCustomAmountInvalid =
    customAmount !== '' && (Number(customAmount) < minAmount || Number(customAmount) > maxAmount);

  useEffect(() => {
    setShouldDisableNext(details.price <= 0);
    return () => setShouldDisableNext(false);
  }, [details.price, setShouldDisableNext]);

  useEffect(() => {
    if (reportAmount && details.price > 0) {
      reportAmount(details.price);
    }
  }, [details.price, reportAmount]);

  const setAmount = (amount: number) => {
    setOrderPageState({ ...orderPageState, details: { ...details, price: amount } });
  };

  const handlePresetClick = (presetAmount: number) => {
    setCustomAmount('');
    setAmount(presetAmount);
  };

  const handleCustomAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setCustomAmount(value);
    const amount = Number(value);
    setAmount(amount >= minAmount && amount <= maxAmount ? amount : 0);
  };

  const amountRange = amountRangeLabel(account, t);

  return (
    <Grid container flexDirection="column" gap={2}>
      <Grid item>
        <Typography sx={{ fontWeight: 700, fontSize: 22 }}>{t('giftCardPage.amountTitle')}</Typography>
        {amountRange && (
          <Typography variant="body2" color="text.secondary">
            {amountRange}
          </Typography>
        )}
      </Grid>
      <Grid item sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
        {presetAmounts.map((presetAmount) => {
          const isSelected = details.price === presetAmount && customAmount === '';
          return (
            <Button
              key={presetAmount}
              variant={isSelected ? 'contained' : 'outlined'}
              color="inherit"
              onClick={() => handlePresetClick(presetAmount)}
              sx={(theme) => ({
                height: 52,
                borderRadius: '14px',
                fontWeight: 700,
                fontSize: 16,
                ...(isSelected
                  ? {
                      backgroundColor: theme.customTheme.palette.primary,
                      color: 'white',
                      boxShadow: '0 6px 14px rgba(0,0,0,0.18)',
                      '&:hover': { backgroundColor: theme.customTheme.palette.primary },
                    }
                  : {
                      backgroundColor: 'white',
                      borderColor: 'grey.300',
                      color: 'text.primary',
                      boxShadow: 'none',
                      '&:hover': { borderColor: theme.customTheme.palette.primary, backgroundColor: 'white' },
                    }),
              })}>
              {customerFacingPriceFormatter(presetAmount, account)}
            </Button>
          );
        })}
      </Grid>
      {settings?.allow_custom_amount && (
        <Grid item>
          <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 1 }}>
            {t('giftCardPage.otherAmount')}
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder={t('giftCardPage.customAmountPlaceholder')}
            value={customAmount}
            onChange={handleCustomAmountChange}
            error={isCustomAmountInvalid}
            InputProps={{
              endAdornment: <InputAdornment position="end">₪</InputAdornment>,
              sx: (theme) => ({
                borderRadius: '14px',
                backgroundColor: 'white',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.customTheme.palette.primary },
              }),
            }}
            helperText={
              isCustomAmountInvalid
                ? t('order_page.amountOutOfRange', {
                    min: customerFacingPriceFormatter(minAmount, account),
                    max: customerFacingPriceFormatter(maxAmount, account),
                  })
                : undefined
            }
          />
        </Grid>
      )}
    </Grid>
  );
};

export default GiftCardAmountStep;
